import { useEnsureQuoteExists } from "../onboarding/useEnsureQuoteExists";
import {
  getGetApiEasyAdministrationAreaQueryKey,
  useGetApiEasyAdministrationArea,
  usePutApiEasyAdministrationArea,
} from "../openapi/backendApi";

import React, { ReactNode, createContext, useContext, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

interface AdministrationAreaContextType {
  administrationArea: string;
  putAdministrationArea: (value: string) => Promise<void>;
  isSuccessGetAdministrationAreaData: boolean;
  isErrorGetAdministrationAreaData: boolean;
  refetchAdministrationArea: () => void;
  administrationCountryIsoCode: string;
  handleChangeCountry: (value: string | number) => void;
}

const AdministrationAreaContext = createContext<
  AdministrationAreaContextType | undefined
>(undefined);

interface AdministrationAreaProviderProps {
  children: ReactNode;
}

export const AdministrationAreaProvider: React.FC<
  AdministrationAreaProviderProps
> = ({ children }) => {
  const queryClient = useQueryClient();
  const savedIsoCode =
    sessionStorage.getItem("administrationCountryIsoCode") || "";
  const [administrationCountryIsoCode, setAdministrationCountryIsoCode] =
    useState<string>(savedIsoCode);
  const handleChangeAdministrationCountryIsoCode = (value: string): void => {
    sessionStorage.setItem("administrationCountryIsoCode", value);
    setAdministrationCountryIsoCode(value);
  };

  // The backend can only report/attach an administration area once a quote
  // exists for the session, so this request must wait for getQuoteId.
  const { isReady: isQuoteReady } = useEnsureQuoteExists();

  const {
    data: administrationAreaData,
    isSuccess: isSuccessGetAdministrationAreaData,
    isError: isErrorGetAdministrationAreaData,
    refetch: refetchAdministrationArea,
  } = useGetApiEasyAdministrationArea({
    query: {
      enabled: isQuoteReady,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  });

  const { mutate: mutateAdministrationArea } = usePutApiEasyAdministrationArea({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetApiEasyAdministrationAreaQueryKey(),
        });
      },
    },
  });

  const handleChangeCountry = (countryIsoCode: string | number): void => {
    let valueStr;
    if (typeof countryIsoCode === "number") {
      valueStr = countryIsoCode.toString();
    } else {
      valueStr = countryIsoCode;
    }
    setAdministrationCountryIsoCode(valueStr);
  };

  const handleMutateAdministrationArea = async (
    newAdmArea: string,
  ): Promise<void> => {
    await mutateAdministrationArea({ data: { string: newAdmArea } });
    handleChangeAdministrationCountryIsoCode(newAdmArea);
  };

  return (
    <AdministrationAreaContext.Provider
      value={{
        administrationArea:
          typeof administrationAreaData?.data?.string === "string"
            ? administrationAreaData?.data?.string
            : "",
        putAdministrationArea: handleMutateAdministrationArea,
        isSuccessGetAdministrationAreaData,
        isErrorGetAdministrationAreaData,
        refetchAdministrationArea: () => {
          refetchAdministrationArea();
        },
        administrationCountryIsoCode,
        handleChangeCountry,
      }}
    >
      {children}
    </AdministrationAreaContext.Provider>
  );
};

export const useAdministrationArea = (): AdministrationAreaContextType => {
  const context = useContext(AdministrationAreaContext);
  if (!context) {
    throw new Error(
      "useAdministrationArea must be used within a AdministrationAreaProvider",
    );
  }
  return context;
};
